import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonSize,
  MibBreakpointService,
  MibButtonDirective,
  MibIconComponent,
  MibTooltipDirective,
  updateTargetHeight,
} from '@medinbox/angular';
import { ChatService } from '../chat.service';
import { PromptExamplesDropdown } from '../prompt-examples-dropdown';

@Component({
  selector: 'mai-prompt-box',
  imports: [MibButtonDirective, MibIconComponent, ReactiveFormsModule, MibTooltipDirective],
  templateUrl: './prompt-box.html',
  styleUrl: './prompt-box.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptBox {
  protected readonly service = inject(ChatService);
  private readonly breakpointService = inject(MibBreakpointService);

  readonly displayPlusButton = input(false, { transform: booleanAttribute });
  readonly placeholder = input<string>('What would you like to know?');

  private readonly defaultTextareaHeight = 20;
  private readonly isCompactHeight = computed(
    () => this.breakpointService.heightClass() === 'compact',
  );
  private readonly notExpanded = computed(() => this.breakpointService.sizeClass() !== 'expanded');
  protected readonly buttonSize = computed<ButtonSize>(() => (this.notExpanded() ? 'sm' : 'md'));
  /** The buttons plus the box's `p-2` on each side: 32 + 16 on a phone, 36 + 16 elsewhere. */
  private readonly defaultPromptHeight = computed(() => (this.notExpanded() ? 48 : 52));
  private readonly plusButton = viewChild('plusButton', { read: ElementRef });
  protected readonly userPrompt = new FormControl('', { nonNullable: true });

  constructor() {
    // The draft outlives the box: a tab left and read again gives back what was being typed.
    effect(() => {
      const draft = this.service.draft();
      untracked(() => {
        if (this.userPrompt.value !== draft) {
          this.userPrompt.setValue(draft, { emitEvent: false });
        }
      });
    });
    this.userPrompt.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((text) => this.service.keepDraft(text));
  }

  readonly textareaElement = viewChild.required<ElementRef<HTMLTextAreaElement>>('textarea');
  readonly biggerPrompt = signal(false);
  private readonly textHeight = signal(0);
  readonly promptBoxHeight = computed(() => this.textHeight() + this.defaultPromptHeight());

  readonly textareaPosition = computed(() => {
    if (this.biggerPrompt()) {
      return 'left-5 pr-6 -mr-6 right-2 top-2 not-height-medium:top-1 not-height-medium:left-3 not-height-medium:right-1';
    }
    // Tailwind emits `not-height-medium:` before `not-expanded:`, so only the compound variant overrides it.
    const left = this.displayPlusButton()
      ? 'left-15 not-expanded:left-14 not-expanded:not-height-medium:left-13'
      : 'left-5';
    return `${left} right-15 not-expanded:right-14 not-expanded:not-height-medium:right-13`;
  });

  sendPrompt() {
    if (!this.service.chatLocked()) {
      this.service.sendMessage(this.userPrompt.getRawValue().trim());
      this.setPrompt('');
      this.resetPromptHeight();
    }
  }

  /**
   * adjust textarea size to its content without going over a certain limit (about 4 lines)
   * @param event
   */
  updatePromptHeight(event: Event): void {
    const newHeight = this.isCompactHeight()
      ? updateTargetHeight(event, this.defaultPromptHeight())
      : updateTargetHeight(event);
    // An empty textarea measures its placeholder, which must never grow the box.
    if (!this.userPrompt.value) {
      this.resetPromptHeight();
    } else if (newHeight > this.defaultTextareaHeight) {
      this.biggerPrompt.set(true);
      this.textHeight.set(newHeight);
    }
  }

  /**
   * Reset prompt height to its default size
   */
  resetPromptHeight(): void {
    this.biggerPrompt.set(false);
    this.textHeight.set(0);
    this.textareaElement().nativeElement.style.height = `${this.defaultTextareaHeight}px`;
  }

  /**
   * Method allowing to set and send a prompt programmatically.
   * @param prompt Prompt to set
   */
  setPrompt(prompt: string): void {
    this.userPrompt.setValue(prompt);
    this.sendPrompt();
  }

  clickOnPlusButton(): void {
    const plusButton = this.plusButton();
    if (plusButton) {
      this.service.openPromptExampleDropdown(plusButton, this, PromptExamplesDropdown);
    }
  }
}
